/**
 * Which Azure the SPA signs in against.
 *
 * **Hand-mirrored from `apps/api/src/shared/cloud.ts`** (ADR-0026), and narrower
 * than it: the browser needs a sign-in host and nothing else. It never builds a
 * storage endpoint — uploads go to a SAS URL the API hands it, host and all
 * (ADR-0008, ADR-0009) — and it never validates a token, so it needs no issuer
 * list and no JWKS URL.
 *
 * Hand-mirrored rather than shared because these are two workspaces with no
 * shared types package, which is the same call `meSchema` already makes for the
 * agency-state enum in `lib/api.ts`. The set is closed and has changed once in
 * the product's life; a package to carry two strings would cost more than the
 * duplication does.
 */

/** Named as the Azure CLI names them, so `az cloud set --name` cannot drift. */
export const AZURE_CLOUDS = ['AzureCloud', 'AzureUSGovernment'] as const;

export type AzureCloud = (typeof AZURE_CLOUDS)[number];

const LOGIN_HOSTS: Record<AzureCloud, string> = {
  AzureCloud: 'login.microsoftonline.com',
  AzureUSGovernment: 'login.microsoftonline.us',
};

/**
 * The sign-in authority host, no scheme.
 *
 * Getting this wrong in government is quieter than the API's version of the
 * same mistake: rather than a 401 in a log, the user is redirected to
 * `login.microsoftonline.com`, where their tenant does not exist, and reads
 * Microsoft's own "we couldn't find an account" page. They conclude their
 * account is broken. See the runbook's §6.2 and §8.
 */
export function loginHost(cloud: AzureCloud): string {
  return LOGIN_HOSTS[cloud];
}
